import { useCallback } from 'react';
import { map, reduce, pickBy } from 'lodash';
import { getImageMaxZoomBySize } from 'modules/campaigns/modules/creative-editor/components/MediaTabs/CroppingToolsForm/utils/cropDataUtils';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import { useCreativesApi } from 'services/api';
import { COMPONENT_STATUS } from 'services/constants';

export const useCropDataInit = ({
    src,
    ratios,
    setOutcroppingStatus,
    cropInit: croppingSavedData,
    defaultCropData,
}) => {
    const { getCropAreaSize, getSingleCropAreaSize } = useCreativesApi();
    const formDataFetcher = useCallback(async () => {
        try {
            const cloudinaryData = await Promise.all(getCropAreaSize(src, ratios));

            const croppingSavedDataMap =
                croppingSavedData?.cropData.reduce((map, item) => {
                    const ratioValue = item.ratio.width / item.ratio.height;
                    if (item.area || item.url) {
                        map[ratioValue] = item;
                    }
                    return map;
                }, {}) || {};

            const replacedImageSavedDataMap = pickBy(croppingSavedDataMap, item => item.url);
            const enrichedReplacedImageSavedData = await Promise.all(
                map(replacedImageSavedDataMap, async (item, ratioValue) => {
                    const replacedImageCloudinaryData = await getSingleCropAreaSize(item.url, ratioValue);
                    const imageMaxZoom = await getImageMaxZoomBySize(item.url);
                    return { ratioValue, imageMaxZoom, replacedImageCloudinaryData };
                })
            );
            const replacedImageDataMap = reduce(
                enrichedReplacedImageSavedData,
                (map, { ratioValue, imageMaxZoom, replacedImageCloudinaryData }) => {
                    map[ratioValue] = { imageMaxZoom, replacedImageCloudinaryData };
                    return map;
                },
                {}
            );

            const cloudinaryDataMap = cloudinaryData?.reduce((map, item) => {
                map[item.ratio] = item;
                return map;
            }, {});

            const unifiedCropInitialData = defaultCropData.map(({ ratio, ...aspectRatioDefaultValues }) => {
                const savedCropData = croppingSavedDataMap[ratio];
                const replacedImageUrl = savedCropData?.url;
                const cloudinaryData = cloudinaryDataMap[ratio];
                const { imageMaxZoom: replacedImageMaxZoom, replacedImageCloudinaryData } =
                    replacedImageDataMap[ratio] || {};
                const initialCroppedAreaPixels = replacedImageUrl
                    ? replacedImageCloudinaryData.resize?.[0]
                    : cloudinaryData.resize?.[0];

                if (croppingSavedDataMap && savedCropData && savedCropData.area) {
                    return {
                        ...aspectRatioDefaultValues,
                        ratio,
                        initialCroppedAreaPixels: {
                            height: savedCropData.area.height,
                            width: savedCropData.area.width,
                            x: savedCropData.area.topLeftX,
                            y: savedCropData.area.topLeftY,
                        },
                        cloudinaryInitialCroppedAreaPixels: initialCroppedAreaPixels,
                        replacedImageUrl,
                        replacedImageMaxZoom,
                    };
                }

                if (cloudinaryDataMap && cloudinaryDataMap[ratio]) {
                    return {
                        ...aspectRatioDefaultValues,
                        ratio,
                        initialCroppedAreaPixels,
                        isFromCloudinary: true,
                        replacedImageUrl,
                        replacedImageMaxZoom,
                    };
                }

                return {};
            });

            setOutcroppingStatus(COMPONENT_STATUS.ACTIVE);
            return { cropData: unifiedCropInitialData };
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [getCropAreaSize, src, ratios, croppingSavedData, setOutcroppingStatus, defaultCropData, getSingleCropAreaSize]);

    useFormState({ formDataFetcher, waitAccountFetched: false });
};
